import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { HATS, HAT_BY_KEY, WEIGHT_WORDS } from "../data/hats";
import { Slider } from "./ui/slider";
import { Checkbox } from "./ui/checkbox";
import CastleGate from "./CastleGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stepVariants = {
  enter: { opacity: 0, y: 28 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -28 },
};

const StepLabel = ({ n, children }) => (
  <p className="font-mono text-xs tracking-[0.35em] text-gold mb-4" data-testid={`step-label-${n}`}>
    {String(n).padStart(2, "0")} — {children}
  </p>
);

const ContinueButton = ({ onClick, disabled, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className="cta-gold font-mono text-xs tracking-[0.2em] px-8 py-3.5 disabled:pointer-events-none"
  >
    {children}
  </button>
);

export default function LoadCheck({ onSeatsChanged }) {
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState([]);
  const [weights, setWeights] = useState({});
  const [hardestText, setHardestText] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [superpower, setSuperpower] = useState("");
  const [favoriteTool, setFavoriteTool] = useState("");
  const [shareOnWall, setShareOnWall] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const ref = useMemo(() => new URLSearchParams(window.location.search).get("ref"), []);

  const toggleHat = (key) => {
    setSelected((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      if (!prev.includes(key)) {
        setWeights((w) => ({ ...w, [key]: w[key] || 3 }));
      }
      return next;
    });
  };

  const profile = useMemo(
    () =>
      selected
        .map((k) => ({ ...HAT_BY_KEY[k], weight: weights[k] || 3 }))
        .sort((a, b) => b.weight - a.weight),
    [selected, weights]
  );
  const heaviest = profile[0];

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        first_name: firstName.trim(),
        hats: selected.map((k) => ({ hat: k, weight: weights[k] || 3 })),
        hardest_text: hardestText.trim(),
        superpower: superpower.trim() || null,
        favorite_tool: favoriteTool.trim() || null,
        share_on_wall: shareOnWall,
        consent,
        ref,
      });
      setResult(res.data);
      setStep(6);
      onSeatsChanged?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("All 100 founding seats have been claimed.");
      } else if (status === 422) {
        toast.error("Please check your details — something doesn't look right.");
      } else {
        toast.error("Something went wrong. Try again in a moment.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inviteLink = result ? `${window.location.origin}/?ref=${result.invite_code}` : "";
  const canSubmit = email.includes("@") && firstName.trim().length > 0 && consent && !submitting;

  return (
    <section id="load-check" data-testid="load-check-section" className="py-28 md:py-36 px-4 sm:px-6 relative">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-14">
          <p className="font-mono text-xs tracking-[0.35em] text-gold mb-6">05 — JOIN THE KEEP</p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
            Sixty seconds. Five questions.
            <br />
            <span className="italic text-gold">One honest picture.</span>
          </h2>
        </div>

        <div className="border border-border bg-card/60 backdrop-blur-sm relative">
          {/* gold corner ticks */}
          <span className="absolute -top-px -left-px w-4 h-4 border-t border-l border-gold" aria-hidden="true" />
          <span className="absolute -top-px -right-px w-4 h-4 border-t border-r border-gold" aria-hidden="true" />
          <span className="absolute -bottom-px -left-px w-4 h-4 border-b border-l border-gold" aria-hidden="true" />
          <span className="absolute -bottom-px -right-px w-4 h-4 border-b border-r border-gold" aria-hidden="true" />

          <div className="p-6 sm:p-10 md:p-14">
            <AnimatePresence mode="wait">
              {step === 1 && (
                <motion.div key="s1" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45 }}>
                  <StepLabel n={1}>PICK YOUR HATS</StepLabel>
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-2">Which hats are you wearing alone right now?</h3>
                  <p className="text-muted-foreground text-sm mb-10">Tap every one that applies.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
                    {HATS.map((h) => {
                      const active = selected.includes(h.key);
                      return (
                        <button
                          key={h.key}
                          data-testid={`hat-card-${h.key}`}
                          onClick={() => toggleHat(h.key)}
                          aria-pressed={active}
                          className={`group border p-5 text-left transition-colors duration-300 ${
                            active ? "border-gold bg-gold/5" : "border-border hover:border-gold/50"
                          }`}
                        >
                          <h.icon
                            strokeWidth={1}
                            className={`w-6 h-6 mb-4 transition-colors duration-300 ${active ? "text-gold" : "text-muted-foreground group-hover:text-gold/70"}`}
                          />
                          <span className={`block font-serif text-lg font-light leading-tight transition-colors duration-300 ${active ? "text-bone" : "text-bone/70"}`}>
                            {h.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground" data-testid="hats-selected-count">
                      {selected.length} SELECTED
                    </span>
                    <ContinueButton testId="step1-continue-button" onClick={() => setStep(2)} disabled={selected.length === 0}>
                      WEIGH THEM
                    </ContinueButton>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div key="s2" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45 }}>
                  <StepLabel n={2}>WEIGH THEM</StepLabel>
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-10">How heavy is each hat right now?</h3>
                  <div className="space-y-8 mb-12">
                    {selected.map((k) => {
                      const h = HAT_BY_KEY[k];
                      const w = weights[k] || 3;
                      return (
                        <div key={k} data-testid={`weight-row-${k}`}>
                          <div className="flex items-baseline justify-between mb-3">
                            <span className="font-serif text-xl font-light">{h.label}</span>
                            <span className={`font-mono text-xs tracking-[0.15em] ${w >= 4 ? "text-gold" : "text-muted-foreground"}`} data-testid={`weight-label-${k}`}>
                              {w} — {WEIGHT_WORDS[w].toUpperCase()}
                            </span>
                          </div>
                          <Slider
                            data-testid={`weight-slider-${k}`}
                            className="vault-slider"
                            min={1}
                            max={5}
                            step={1}
                            value={[w]}
                            onValueChange={([v]) => setWeights((prev) => ({ ...prev, [k]: v }))}
                          />
                          <div className="flex justify-between mt-2 font-mono text-[10px] text-muted-foreground/60">
                            <span>LIGHT</span>
                            <span>CRUSHING</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between">
                    <button data-testid="step2-back-button" onClick={() => setStep(1)} className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors duration-300">
                      ← BACK
                    </button>
                    <ContinueButton testId="step2-continue-button" onClick={() => setStep(3)}>
                      SEE MY LOAD PROFILE
                    </ContinueButton>
                  </div>
                </motion.div>
              )}

              {step === 3 && heaviest && (
                <motion.div key="s3" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45 }}>
                  <StepLabel n={3}>YOUR LOAD PROFILE</StepLabel>
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-2" data-testid="load-profile-headline">
                    You're carrying {profile.length} {profile.length === 1 ? "hat" : "hats"} alone. Your heaviest:{" "}
                    <span className="italic text-gold">{heaviest.label}.</span>
                  </h3>
                  <p className="text-muted-foreground text-sm mb-10" data-testid="load-profile-reassurance">
                    {heaviest.reassurance}
                  </p>
                  <div className="space-y-4 mb-12">
                    {profile.map((h, i) => (
                      <div key={h.key} data-testid={`load-bar-${h.key}`}>
                        <div className="flex items-baseline justify-between mb-1.5">
                          <span className="font-mono text-xs tracking-[0.15em] text-bone/80">{h.label.toUpperCase()}</span>
                          <span className="font-mono text-xs text-gold">{h.weight}/5</span>
                        </div>
                        <div className="h-[3px] bg-secondary w-full">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${h.weight * 20}%` }}
                            transition={{ duration: 0.9, delay: 0.15 + i * 0.1, ease: [0.33, 1, 0.68, 1] }}
                            className="h-full bg-gold"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <button data-testid="step3-back-button" onClick={() => setStep(2)} className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors duration-300">
                      ← ADJUST
                    </button>
                    <ContinueButton testId="step3-continue-button" onClick={() => setStep(4)}>
                      CONTINUE
                    </ContinueButton>
                  </div>
                </motion.div>
              )}

              {step === 4 && heaviest && (
                <motion.div key="s4" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45 }}>
                  <StepLabel n={4}>SAY IT IN YOUR OWN WORDS</StepLabel>
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-8">
                    What's the hardest part of being your own{" "}
                    <span className="italic text-gold">{heaviest.lead}</span>?
                  </h3>
                  <textarea
                    data-testid="hardest-text-input"
                    value={hardestText}
                    onChange={(e) => setHardestText(e.target.value)}
                    rows={5}
                    maxLength={2000}
                    placeholder="Be specific. This is the part we actually read."
                    className="w-full bg-background border border-border focus:border-gold outline-none p-5 font-serif text-lg font-light text-bone placeholder:text-muted-foreground/50 placeholder:italic resize-none transition-colors duration-300 mb-10"
                  />
                  <div className="flex items-center justify-between">
                    <button data-testid="step4-back-button" onClick={() => setStep(3)} className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors duration-300">
                      ← BACK
                    </button>
                    <ContinueButton testId="step4-continue-button" onClick={() => setStep(5)} disabled={hardestText.trim().length < 2}>
                      ALMOST THERE
                    </ContinueButton>
                  </div>
                </motion.div>
              )}

              {step === 5 && (
                <motion.div key="s5" variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.45 }}>
                  <StepLabel n={5}>SAVE YOUR FREE SEAT</StepLabel>
                  <h3 className="font-serif text-2xl sm:text-3xl font-light mb-10">Where should we send your Load Profile?</h3>
                  <div className="space-y-5 mb-8">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="lc-email" className="block font-mono text-[11px] tracking-[0.2em] text-muted-foreground mb-2">EMAIL *</label>
                        <input
                          id="lc-email"
                          data-testid="email-input"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@yourcompany.com"
                          className="w-full bg-background border border-border focus:border-gold outline-none px-4 py-3 text-sm text-bone placeholder:text-muted-foreground/40 transition-colors duration-300"
                        />
                      </div>
                      <div>
                        <label htmlFor="lc-first-name" className="block font-mono text-[11px] tracking-[0.2em] text-muted-foreground mb-2">FIRST NAME *</label>
                        <input
                          id="lc-first-name"
                          data-testid="first-name-input"
                          type="text"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          placeholder="So replies sound human"
                          className="w-full bg-background border border-border focus:border-gold outline-none px-4 py-3 text-sm text-bone placeholder:text-muted-foreground/40 transition-colors duration-300"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="lc-superpower" className="block font-mono text-[11px] tracking-[0.2em] text-muted-foreground mb-2">ONE THING I'M GOOD AT</label>
                        <input
                          id="lc-superpower"
                          data-testid="superpower-input"
                          type="text"
                          value={superpower}
                          onChange={(e) => setSuperpower(e.target.value)}
                          placeholder="Optional — helps us match you"
                          className="w-full bg-background border border-border focus:border-gold outline-none px-4 py-3 text-sm text-bone placeholder:text-muted-foreground/40 transition-colors duration-300"
                        />
                      </div>
                      <div>
                        <label htmlFor="lc-fav-tool" className="block font-mono text-[11px] tracking-[0.2em] text-muted-foreground mb-2">A TOOL I COULDN'T LIVE WITHOUT</label>
                        <input
                          id="lc-fav-tool"
                          data-testid="favorite-tool-input"
                          type="text"
                          value={favoriteTool}
                          onChange={(e) => setFavoriteTool(e.target.value)}
                          placeholder="Optional — seeds the tool library"
                          className="w-full bg-background border border-border focus:border-gold outline-none px-4 py-3 text-sm text-bone placeholder:text-muted-foreground/40 transition-colors duration-300"
                        />
                      </div>
                    </div>
                    <div className="flex items-start gap-3 pt-2">
                      <Checkbox
                        id="lc-wall"
                        data-testid="wall-share-checkbox"
                        className="vault-checkbox mt-0.5"
                        checked={shareOnWall}
                        onCheckedChange={(v) => setShareOnWall(v === true)}
                      />
                      <label htmlFor="lc-wall" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                        Share my answer anonymously on the wall. <span className="text-muted-foreground/60">(Reviewed by Ryan before it appears.)</span>
                      </label>
                    </div>
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="lc-consent"
                        data-testid="consent-checkbox"
                        className="vault-checkbox mt-0.5"
                        checked={consent}
                        onCheckedChange={(v) => setConsent(v === true)}
                      />
                      <label htmlFor="lc-consent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                        I agree my answers stay private and are used only to shape The Keep. *
                      </label>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <button data-testid="step5-back-button" onClick={() => setStep(4)} className="font-mono text-[11px] tracking-[0.2em] text-muted-foreground hover:text-gold transition-colors duration-300">
                      ← BACK
                    </button>
                    <ContinueButton testId="save-seat-button" onClick={submit} disabled={!canSubmit}>
                      {submitting ? "SAVING…" : "SAVE MY FREE SEAT"}
                    </ContinueButton>
                  </div>
                </motion.div>
              )}

              {step === 6 && result && (
                <motion.div key="s6" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                  <CastleGate
                    seat={result.seat_number}
                    inviteLink={inviteLink}
                    alreadyRegistered={result.already_registered}
                    emailSent={result.email_sent}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
